import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    IconButton,
    Divider,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    Info,
    Timer,
    TrendingUp,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// Backend uses *Minutes fields, form uses *Seconds — flip them back so the
// server-side error highlights the actual input the user can edit.
const OCPP_TEMPLATE_PATH_ALIAS: Record<string, string> = {
    approachingThresholdMinutes: 'approachingThresholdSeconds',
    nearThresholdMinutes: 'nearThresholdSeconds',
};

const mapOCPPTemplateBackendPath = (path: string): string => {
    const stripped = path
        .replace(/^body\./, '')
        .replace(/^params\./, '')
        .replace(/^query\./, '');
    return OCPP_TEMPLATE_PATH_ALIAS[stripped] || stripped;
};

const OCPP_TEMPLATE_KNOWN_FIELDS = new Set([
    'templateName', 'description', 'ocppVersion', 'ocppProtocol',
    'normalIntervalSeconds', 'initialFastIntervalEnabled',
    'initialFastIntervalSeconds', 'initialFastIntervalCount',
    'approachingIntervalEnabled', 'approachingIntervalSeconds',
    'approachingThresholdSeconds', 'nearIntervalEnabled',
    'nearIntervalSeconds', 'nearThresholdSeconds',
    'hysteresisEnabled', 'hysteresisMinutes',
    'powerSmoothingEnabled', 'powerSmoothingFactor', 'isActive',
]);

const ocppTemplateFieldLabelKey = (field: string): string =>
    OCPP_TEMPLATE_KNOWN_FIELDS.has(field)
        ? `resources.ocpp-templates.fields.${field}`
        : field;

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`template-tabpanel-${index}`}
            aria-labelledby={`template-tab-${index}`}
            style={{ maxWidth: '100%' }}
            {...other}
        >
            {value === index && <Box sx={{ pt: { xs: 2, sm: 3 }, maxWidth: '100%' }}>{children}</Box>}
        </div>
    );
};

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#009A75',
                    '&:hover': {
                        backgroundColor: '#007F5F',
                        boxShadow: '0 4px 12px rgba(0,154,117,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/ocpp-templates')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.ocpp-templates.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.ocpp-templates.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {translate('resources.ocpp-templates.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const OCPPTemplatesCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [tabValue, setTabValue] = useState(0);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSave = (data: any) => {
        // Convert seconds (form field) to minutes (API field). Pass the raw
        // value through whenever it's defined — including 0 — so the backend
        // can reject it via Zod instead of us silently coercing falsy inputs
        // to `undefined` and stripping the field from the payload.
        const finalData = {
            ...data,
            approachingThresholdMinutes:
                data.approachingThresholdSeconds === undefined || data.approachingThresholdSeconds === null
                    ? data.approachingThresholdSeconds
                    : Number(data.approachingThresholdSeconds) / 60,
            nearThresholdMinutes:
                data.nearThresholdSeconds === undefined || data.nearThresholdSeconds === null
                    ? data.nearThresholdSeconds
                    : Number(data.nearThresholdSeconds) / 60,
        };

        // Remove the seconds fields as we send minutes to the API
        delete finalData.approachingThresholdSeconds;
        delete finalData.nearThresholdSeconds;

        return finalData;
    };

    const defaultValues = {
        ocppVersion: '1.6',
        ocppProtocol: 'json',
        normalIntervalSeconds: 30,
        initialFastIntervalEnabled: true,
        initialFastIntervalSeconds: 5,
        initialFastIntervalCount: 10,
        approachingIntervalEnabled: true,
        approachingIntervalSeconds: 5,
        approachingThresholdSeconds: 300, // 5 minutes
        nearIntervalEnabled: true,
        nearIntervalSeconds: 1,
        nearThresholdSeconds: 60, // 1 minute
        hysteresisEnabled: true,
        hysteresisMinutes: 2,
        powerSmoothingEnabled: true,
        powerSmoothingFactor: 0.7,
        isActive: true,
    };

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={handleSave}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.ocpp-templates.notifications.created', { type: 'success' });
                    redirect('/ocpp-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapOCPPTemplateBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(ocppTemplateFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    minWidth: 0,
                },
                '& .RaCreate-card': {
                    maxWidth: '100%',
                    overflow: 'hidden',
                    minWidth: 0,
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                defaultValues={defaultValues}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant={isMobile ? "fullWidth" : "scrollable"}
                            scrollButtons={isMobile ? false : "auto"}
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: { xs: 40, sm: 48 },
                                '& .MuiTab-root': {
                                    minWidth: { xs: 0, sm: 90 },
                                    minHeight: { xs: 40, sm: 48 },
                                    px: { xs: 0.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1.5 },
                                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                    lineHeight: 1.1,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textTransform: 'none',
                                },
                            }}
                        >
                            <Tab label={translate('resources.ocpp-templates.tabs.general')} />
                            <Tab label={translate('resources.ocpp-templates.tabs.intervals')} />
                            <Tab label={translate('resources.ocpp-templates.tabs.advanced')} />
                        </Tabs>
                    </Box>
                    
                    <TabPanel value={tabValue} index={0}>
                        {/* Basic Information */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Info sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.basic')}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="templateName"
                                            validate={[
                                                required(),
                                                maxLength(255, translate("resources.ocpp-templates.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.templateName')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="description"
                                            multiline
                                            rows={3}
                                            validate={[
                                                maxLength(500, translate("resources.ocpp-templates.validation.descriptionTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.description')}
                                        />
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <SelectInput
                                                source="ocppVersion"
                                                choices={[
                                                    { id: '1.6', name: translate('resources.ocpp-templates.ocppVersions.1.6') },
                                                    { id: '2.0.1', name: translate('resources.ocpp-templates.ocppVersions.2.0.1') },
                                                ]}
                                                validate={[required()]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.ocppVersion')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <SelectInput
                                                source="ocppProtocol"
                                                choices={[
                                                    { id: 'json', name: translate('resources.ocpp-templates.ocppProtocols.json') },
                                                    { id: 'soap', name: translate('resources.ocpp-templates.ocppProtocols.soap') },
                                                ]}
                                                validate={[required()]}
                                                fullWidth
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                    
                                    <BooleanInput
                                        source="isActive"
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={1}>
                        {/* Interval Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Timer sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.intervals')}
                                    </Typography>
                                </Box>
                                
                                <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                    {translate('resources.ocpp-templates.intervalExplanation')}
                                </Alert>

                                {/* Normal Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.normalInterval')}
                                    </Typography>
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="normalIntervalSeconds"
                                            validate={[
                                                required(),
                                                minValue(10, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                maxValue(300, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.normalIntervalSeconds')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Initial Fast Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.initialFastInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="initialFastIntervalEnabled"
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="initialFastIntervalSeconds"
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(30, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.initialFastIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="initialFastIntervalCount"
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.countTooLow")),
                                                    maxValue(50, translate("resources.ocpp-templates.validation.countTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.initialFastIntervalCount')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Approaching Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.approachingInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="approachingIntervalEnabled"
                                        label={translate('resources.ocpp-templates.fields.approachingIntervalEnabled')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="approachingIntervalSeconds"
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(30, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.approachingIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="approachingThresholdSeconds"
                                                validate={[
                                                    minValue(60, translate("resources.ocpp-templates.validation.thresholdTooLow")),
                                                    maxValue(1800, translate("resources.ocpp-templates.validation.thresholdTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.approachingThresholdMinutes')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Near Interval */}
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.nearInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="nearIntervalEnabled"
                                        label={translate('resources.ocpp-templates.fields.nearIntervalEnabled')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="nearIntervalSeconds"
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(10, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.nearIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="nearThresholdSeconds"
                                                validate={[
                                                    minValue(30, translate("resources.ocpp-templates.validation.thresholdTooLow")),
                                                    maxValue(600, translate("resources.ocpp-templates.validation.thresholdTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.nearThresholdMinutes')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={2}>
                        {/* Advanced Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <TrendingUp sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.advanced')}
                                    </Typography>
                                </Box>
                                
                                {/* Hysteresis */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.hysteresis')}
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {translate('resources.ocpp-templates.hysteresisExplanation')}
                                    </Alert>
                                    <BooleanInput
                                        source="hysteresisEnabled"
                                        label={translate('resources.ocpp-templates.fields.hysteresisEnabled')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="hysteresisMinutes"
                                            label={translate('resources.ocpp-templates.fields.hysteresisMinutes')}
                                            validate={[
                                                minValue(0.5, translate("resources.ocpp-templates.validation.hysteresisTooLow")),
                                                maxValue(10, translate("resources.ocpp-templates.validation.hysteresisTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.hysteresisMinutes')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Power Smoothing */}
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.powerSmoothing')}
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {translate('resources.ocpp-templates.powerSmoothingExplanation')}
                                    </Alert>
                                    <BooleanInput
                                        source="powerSmoothingEnabled"
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="powerSmoothingFactor"
                                            label={translate('resources.ocpp-templates.fields.powerSmoothingFactor')}
                                            validate={[
                                                minValue(0, translate("resources.ocpp-templates.validation.smoothingTooLow")),
                                                maxValue(1, translate("resources.ocpp-templates.validation.smoothingTooHigh")),
                                            ]}
                                            step={0.1}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.powerSmoothingFactor')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Box>
            </SimpleForm>
        </Create>
    );
};